import React, { useCallback, useMemo, useRef } from 'react';

type TranscriptLine = {
  seq: number | string;
  text: string;
  tsMs?: number;
};

type TranscriptionBoxProps = {
  title?: string;
  errorText?: string | null;
  warningText?: string | null;
  lines: TranscriptLine[];
  partialText?: string | null;
  emptyText?: string;
};

export function TranscriptionBox({
  errorText,
  warningText,
  lines,
  partialText,
  emptyText = 'No transcript yet.',
}: TranscriptionBoxProps) {
  const isRtlText = useCallback((text: string) => {
    const rtlCharRegex = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return rtlCharRegex.test(text);
  }, []);

  const isArabicText = useCallback((text: string) => {
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(text);
  }, []);

  const displayLines = useMemo(() => {
    if (!lines.length) return [];
    const combined: TranscriptLine[] = [];
    let lastTs: number | null = null;
    for (const line of lines) {
      const hasTs = typeof line.tsMs === 'number';
      if (
        combined.length > 0 &&
        hasTs &&
        lastTs !== null &&
        line.tsMs! - lastTs <= 3000
      ) {
        const prev = combined[combined.length - 1];
        combined[combined.length - 1] = {
          ...prev,
          text: `${prev.text} ${line.text}`.trim(),
          tsMs: line.tsMs,
        };
        lastTs = line.tsMs!;
      } else {
        combined.push(line);
        lastTs = hasTs ? (line.tsMs as number) : null;
      }
    }
    return combined;
  }, [lines]);

  const keepTrailingEmptyRef = useRef(false);

  const maxWordsPerLine = 10;
  const builtLines = useMemo(() => {
    const rows: { tokens: { text: string; isPartial?: boolean }[] }[] = [];

    const pushWords = (words: string[], markPartial = false) => {
      for (const word of words) {
        if (!word) continue;
        const lastIdx = rows.length - 1;
        if (lastIdx >= 0) {
          const count = rows[lastIdx].tokens.length;
          if (count < maxWordsPerLine) {
            rows[lastIdx].tokens.push({ text: word, isPartial: markPartial || undefined });
            continue;
          }
        }
        rows.push({ tokens: [{ text: word, isPartial: markPartial || undefined }] });
      }
    };

    displayLines.forEach((line) => {
      if (line.text.trim() === '.') return;
      const words = line.text.trim().split(/\s+/).filter(Boolean);
      if (!rows.length) rows.push({ tokens: [] });
      pushWords(words);
    });

    let overflowedToNewLine = false;
    if (partialText !== undefined && partialText !== null) {
      const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();
      const trimmedPartial = normalize(partialText);
      if (trimmedPartial === '.') {
        return rows;
      }
      const words = trimmedPartial.split(/\s+/).filter(Boolean);
      const lastIdx = rows.length - 1;
      const lastLineText =
        lastIdx >= 0
          ? normalize(rows[lastIdx].tokens.map((t) => t.text).join(' '))
          : '';

      if (lastLineText) {
        if (trimmedPartial.startsWith(lastLineText)) {
          // Replace the last line with the full partial to avoid duplicate overlap
          rows[lastIdx].tokens = words.map((w) => ({ text: w, isPartial: true }));
          return rows;
        } else if (lastLineText.startsWith(trimmedPartial)) {
          rows[lastIdx].tokens = words.map((w) => ({ text: w, isPartial: true }));
          return rows;
        } else {
          const lastCount = lastIdx >= 0 ? rows[lastIdx].tokens.length : 0;
          if (lastCount >= maxWordsPerLine) {
            rows.push({ tokens: [] });
            overflowedToNewLine = true;
          }
          if (words.length) {
            pushWords(words, true);
            if (lastCount >= maxWordsPerLine) overflowedToNewLine = true;
          } else if (lastCount >= maxWordsPerLine) {
            overflowedToNewLine = true;
          }
        }
      } else {
        const lastCount = lastIdx >= 0 ? rows[lastIdx].tokens.length : 0;
        if (lastCount >= maxWordsPerLine) {
          rows.push({ tokens: [] });
          overflowedToNewLine = true;
        }
        if (words.length) {
          pushWords(words, true);
          if (lastCount >= maxWordsPerLine) overflowedToNewLine = true;
        } else if (lastCount >= maxWordsPerLine) {
          overflowedToNewLine = true;
        }
      }
    }

    if (partialText) {
      if (overflowedToNewLine) keepTrailingEmptyRef.current = true;
    } else {
      keepTrailingEmptyRef.current = false;
    }

    if (keepTrailingEmptyRef.current && rows[rows.length - 1]?.tokens.length) {
      rows.push({ tokens: [] });
    }

    return rows;
  }, [displayLines, partialText]);

  const fontSizePx = 17;
  const arabicFontSizePx = 19;
  const lineHeightPx = 22;
  const visibleLines = 2;
  const containerHeightPx = lineHeightPx * visibleLines;
  const targetOffsetPx = Math.max(0, (builtLines.length - visibleLines) * lineHeightPx);
  const offsetPx = targetOffsetPx;

  return (
    <div className="w-100 d-flex justify-content-center">
      <div
        className="text-white rounded"
        style={{
          width: "80%",
          maxWidth: "720px",
          backgroundColor: "transparent",
          padding: "4px 8px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          {errorText && (
            <span className="text-warning small">{errorText}</span>
          )}
        </div>
        {warningText && (
          <div className="text-warning small">
            {warningText}
          </div>
        )}
        <div
          className="transcription-scroll"
          style={{
            height: `${containerHeightPx}px`,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              transform: `translateY(-${offsetPx}px)`,
              transition: "transform 500ms ease",
            }}
          >
            {builtLines.map((line, idx) => {
              const lineText = line.tokens.map((t) => t.text).join(' ');
              const isArabic = isArabicText(lineText);
              return (
              <div
                key={`${idx}-${lineText}`}
                dir={isRtlText(lineText) ? "rtl" : "ltr"}
                style={{
                  textAlign: isRtlText(lineText) ? "right" : "left",
                  minHeight: `${lineHeightPx}px`,
                  lineHeight: `${lineHeightPx}px`,
                  fontSize: `${isArabic ? arabicFontSizePx : fontSizePx}px`,
                }}
              >
                {line.tokens.length ? (
                  line.tokens.map((token, tokenIdx) => (
                    <span
                      key={`${idx}-${tokenIdx}-${token.text}`}
                      style={{ opacity: token.isPartial ? 0.6 : 1 }}
                    >
                      {token.text}
                      {tokenIdx < line.tokens.length - 1 ? ' ' : ''}
                    </span>
                  ))
                ) : (
                  '\u00A0'
                )}
              </div>
            )})}
            {!lines.length && !partialText && (
              <div className="text-muted" style={{ lineHeight: `${lineHeightPx}px` }}>
                {emptyText}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
